<template>
  <td class="select-entity-cell" :style="{ backgroundColor: backgroundColor || '' }" @click="focusCell()">
    <div class="d-flex align-center ga-2">
      <div
        ref="cell"
        class="select-entity-container d-flex align-center ga-2"
        :id="id"
        :tabindex="id"
        @keydown="onKeydown"
      >
        <select-data-type
          :currentDataType="field.dataType"
          @onSelectDataType="onSelectDataType"
          @update:menuOpen="isDataTypeMenuOpen = $event"
        >
          <v-chip
            size="small"
            density="compact"
            @click="() => {}"
            :class="{ 'show-on-hover-only': !relatedEntity && !field.dataType && !isDataTypeMenuOpen }"
          >
            <span class="text-capitalize">{{ relatedEntity?.name || field.dataType || 'Set data type' }}</span>
            <template v-if="!!relatedEntity && !store.state.isWorkflowDisabled" #close>
              <v-icon
                icon="mdi-close-circle"
                color="#888"
                :size="16"
                @click.stop="onClearRelatedEntity"
                class="remove-entity-icon"
              />
            </template>
          </v-chip>
        </select-data-type>
        <select-entity
          ref="entitySelector"
          :entityId="entityId"
          :allowReselectCurrent="!!pendingDataType"
          @onSelectEntity="onSelectRelatedEntity"
          @onClose="onEntitySelectorClose"
        >
          <!-- Hidden activator, opened programmatically -->
          <span style="display: none;"></span>
        </select-entity>
        <toggle
          v-if='isEntity && entityId'
          mandatory
          :value="field.cardinality"
          :options="FIELD_CARDINALITY_OPTIONS"
          :disabled="store.state.isWorkflowDisabled"
          @update:modelValue="onSelectCardinality"
        />
      </div>
    </div>
  </td>
</template>

<script setup>
import { computed, ref, useTemplateRef, nextTick } from 'vue'
import { useStore } from 'vuex'
import Toggle from '@/components/Toggle.vue'
import SelectEntity from '@/components/SelectEntity'
import SelectDataType from '@/components/SelectDataType.vue'
import { EventBus } from '@/event-bus/event-bus'
import useEntityActions from '@/composables/useEntityActions'
import { DataType, FIELD_CARDINALITY_OPTIONS } from '@/constants/schema'

const { id, entityId, field, fieldIndex, schemaId, backgroundColor } = defineProps({
  id: Number,
  entityId: String,
  field: Object,
  fieldIndex: Number,
  schemaId: String,
  isEntity: Boolean,
  backgroundColor: String
})
const emit = defineEmits(['leave-cell'])

const store = useStore()
const cell = useTemplateRef('cell')
const entitySelector = useTemplateRef('entitySelector')
const workflow = computed(() => store.state.workflow.workflow)
const { selectRelatedEntity, selectCardinality, setDataType } = useEntityActions()

const loadingCardinality = ref(false)
const pendingDataType = ref(null)
const isDataTypeMenuOpen = ref(false)

const relatedEntity = computed(() => {
  return (workflow.value.schemas ?? []).find((entity) => entity.id === entityId)
})

const focusCell = () => {
  cell.value.focus()
}

const onKeydown = (event) => {
  event.stopPropagation()

  switch (event.keyCode) {
    case 13: // enter
    case 37: // left
    case 38: // up
    case 39: // right
    case 40: // down
      emit('leave-cell', event)
  }
}

const onSelectDataType = async (dataType) => {
  if (dataType === DataType.OBJECT) {
    pendingDataType.value = dataType
    await nextTick()
    if (entitySelector.value?.openMenu !== undefined) {
      entitySelector.value.openMenu = true
    }
  } else {
    if (field.dataType === dataType) {
      return
    }
    await setDataType(schemaId, fieldIndex, dataType, null)
    EventBus.$emit('refresh-entity-relationship-diagram')
  }
}

const onClearRelatedEntity = async () => {
  pendingDataType.value = null
  await selectRelatedEntity(null, schemaId, fieldIndex)
  EventBus.$emit('refresh-entity-relationship-diagram')
}

const onSelectRelatedEntity = async (entity) => {
  if (pendingDataType.value && entity) {
    await setDataType(schemaId, fieldIndex, pendingDataType.value, entity.id)
    pendingDataType.value = null
  } else {
    await selectRelatedEntity(entity, schemaId, fieldIndex)
  }
  EventBus.$emit('refresh-entity-relationship-diagram')
}

const onEntitySelectorClose = () => {
  pendingDataType.value = null
}

const onSelectCardinality = async (cardinality) => {
  if (loadingCardinality.value) {
    return
  }

  loadingCardinality.value = true
  await selectCardinality(cardinality, schemaId, entityId)
  loadingCardinality.value = false
}
</script>

<style scoped>
.select-entity-cell {
  border-right: 1px solid #E5E3E1;

  .select-entity-container {
    width: fit-content;
  }
}

:deep(.v-chip) {
  .remove-entity-icon {
    font-size: 0 !important;
    width: 0 !important;
    transition: all 0.2s;
  }

  &:hover {
    .remove-entity-icon {
      font-size: 16px !important;
      width: 16px !important;
    }
  }

  &.show-on-hover-only {
    opacity: 0;
    transition: opacity 0.2s;
  }
}

.select-entity-cell:hover :deep(.v-chip.show-on-hover-only),
.select-entity-container:hover :deep(.v-chip.show-on-hover-only) {
  opacity: 1;
}
</style>
